import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext'; // Corrected path
import OxygenTankForm from './components/OxygenTankForm';
import NasalCannulaForm from './components/NasalCannulaForm';
import NonRebreatherForm from './components/NonRebreatherForm';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import UnauthorizedPage from './pages/UnauthorizedPage';
import './App.css';
import ThemeSwitcher from './components/ThemeSwitcher';
import SkillSheetsDashboard from './pages/SkillSheetsDashboard'; // Import the new dashboard
import InstructorDashboard from './pages/InstructorDashboard';
import StudentDashboard from './pages/StudentDashboard';
import SkillSheetBuilder from './pages/SkillSheetBuilder'; // Will be renamed to SkillSheetBuilder
import ScenarioBuilder from './pages/ScenarioBuilder';
import InstructorSkillSheets from './pages/InstructorSkillSheets';
import InstructorScenarioBuilder from './pages/InstructorScenarioBuilder';
import UserManagement from './pages/UserManagement';
import LabAssignmentsDashboard from './pages/LabAssignmentsDashboard';
import NewLabDay from './pages/NewLabDay';
import ClassroomManagement from './pages/ClassroomManagement';
import NREMTDashboard from './pages/NREMTDashboard';
import NREMTBuilder from './pages/NREMTBuilder';
import NREMTPrintForm from './pages/NREMTPrintForm';
import NREMTPrintBuilderNew from './pages/NREMTPrintBuilderNew';
import NREMTManualAssessment from './pages/NREMTManualAssessment';
import BatchTool from './pages/BatchTool';

// Import debug utility for Maria's permissions (for testing)
import './utils/testMariaPermissions';

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { loading, userRole, currentUser } = useAuth();

  // Show loading spinner while authentication state is being determined
  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <div className="mt-4 text-lg">Initializing application...</div>
        </div>
      </div>
    );
  }

  // Dashboard redirect component based on user role
  const DashboardRedirect = () => {
    const { pathname } = window.location;
    
    // Only redirect from the root path - preserve all other navigation
    if (pathname !== '/') {
      return null;
    }
    
    // Wait for userRole to be loaded before redirecting
    if (loading || !currentUser) {
      return (
        <div className="min-h-screen bg-bg text-text flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <div className="mt-4 text-lg">Loading dashboard...</div>
          </div>
        </div>
      );
    }
    
    // Role-based dashboard routing
    switch (userRole) {
      case 'Admin':
      case 'owner':
        return <Navigate to="/admin" replace />;
      
      case 'Instructor':
        return <Navigate to="/instructor-dashboard" replace />;
      
      case 'student':
        return <Navigate to="/student-dashboard" replace />;
      
      default:
        console.warn(`Unknown user role: ${userRole}. Redirecting to login.`);
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Router>
      <div className="App">
        <Nav />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected Routes */}
          {/* All authenticated users can access these */}
          <Route element={<ProtectedRoute allowedRoles={['student', 'Instructor', 'Admin', 'owner']} />}>
            <Route path="/" element={<DashboardRedirect />} />
            <Route path="/student-dashboard" element={<StudentDashboard />} />
            <Route path="/instructor-dashboard" element={<InstructorDashboard />} />
            <Route path="/skill-sheets" element={<SkillSheetsDashboard />} />
            <Route path="/oxygen-skill" element={<OxygenTankForm />} />
            <Route path="/nasal-cannula" element={<NasalCannulaForm />} />
            <Route path="/non-rebreather" element={<NonRebreatherForm />} />
          </Route>

          {/* Instructor and Admin routes */}
          <Route element={<ProtectedRoute allowedRoles={['Instructor', 'Admin', 'owner']} />}>
            <Route path="/instructor/skill-sheets" element={<InstructorSkillSheets />} />
            <Route path="/instructor/skill-sheets/:templateId" element={<InstructorSkillSheets />} />
            <Route path="/instructor/skill-sheets/create" element={<SkillSheetBuilder />} />
            <Route path="/instructor/skill-sheets/edit/:templateId" element={<SkillSheetBuilder />} />
            <Route path="/instructor/scenario-builder" element={<InstructorScenarioBuilder />} />
            {/* NREMT Builder routes */}
            <Route path="/instructor/nremt-builder" element={<NREMTDashboard />} />
            <Route path="/instructor/nremt-builder/create" element={<NREMTBuilder />} />
            <Route path="/instructor/nremt-builder/edit/:templateId" element={<NREMTBuilder />} />
            <Route path="/instructor/nremt-print/:id" element={<NREMTPrintForm />} />
            <Route path="/instructor/nremt-print-builder" element={<NREMTPrintBuilderNew />} />
            <Route path="/instructor/nremt-print-builder/create" element={<NREMTPrintBuilderNew />} />
            <Route path="/instructor/nremt-print-builder/edit/:id" element={<NREMTPrintBuilderNew />} />
            <Route path="/instructor/nremt-assessment-sheet" element={<NREMTManualAssessment />} />
            <Route path="/instructor/nremt-assessment-sheet/responsive" element={<NREMTManualAssessment formType="responsive" />} />
          </Route>

          {/* Admin and Owner only routes */}
          <Route element={<ProtectedRoute allowedRoles={['Admin', 'owner']} />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/skill-sheet-builder/:templateId?" element={<SkillSheetBuilder />} />
            <Route path="/lab-assignments" element={<LabAssignmentsDashboard />} />
            <Route path="/lab-assignments/new" element={<NewLabDay />} />
            <Route path="/lab-assignments/edit/:labId" element={<NewLabDay />} />
            <Route path="/classroom-management" element={<ClassroomManagement />} />
            {/* Admin NREMT Builder routes */}
            <Route path="/nremt-dashboard" element={<NREMTDashboard />} />
            <Route path="/nremt-builder" element={<NREMTBuilder />} />
            <Route path="/nremt-builder/create" element={<NREMTBuilder />} />
            <Route path="/nremt-builder/edit/:templateId" element={<NREMTBuilder />} />
            <Route path="/nremt-print/:id" element={<NREMTPrintForm />} />
            <Route path="/nremt-print-builder" element={<NREMTPrintBuilderNew />} />
            <Route path="/nremt-print-builder/create" element={<NREMTPrintBuilderNew />} />
            <Route path="/nremt-print-builder/edit/:id" element={<NREMTPrintBuilderNew />} />
            <Route path="/nremt-assessment-sheet" element={<NREMTManualAssessment />} />
            <Route path="/nremt-assessment-sheet/responsive" element={<NREMTManualAssessment formType="responsive" />} />
            <Route path="/batch-tool" element={<BatchTool />} />
          </Route>

          {/* Scenario Builder routes - accessible to all instructor levels */}
          <Route element={<ProtectedRoute allowedRoles={['Instructor', 'Admin', 'owner']} />}>
            <Route path="/scenario-builder" element={<ScenarioBuilder />} />
            <Route path="/scenario-builder/new" element={<ScenarioBuilder />} />
            <Route path="/scenario-builder/edit/:templateId" element={<ScenarioBuilder />} />
            <Route path="/scenario-builder/:templateId" element={<ScenarioBuilder />} />
          </Route>
          
          {/* No fallback route - let React Router handle unknown routes */}
        </Routes>
      </div>
    </Router>
  );
}

const Nav = () => {
  const { currentUser, logout, userRole } = useAuth();

  return (
    <nav className="sticky top-0 z-30 w-full bg-primary/90 backdrop-blur shadow-lg mb-4 transition-all duration-300 no-print">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between px-2 sm:px-6 py-2 sm:py-3 gap-y-2 gap-x-2 min-w-0">
        <div className="flex flex-wrap items-center gap-2 sm:gap-8 min-w-0">
          {currentUser && (userRole === 'Admin' || userRole === 'owner') && (
            <>
              <Link to="/admin" className="flex items-center gap-1 text-base sm:text-lg font-semibold text-text hover:text-secondary transition duration-200 hover:scale-105 whitespace-nowrap px-2 py-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
                Admin
              </Link>
              <Link to="/instructor-dashboard" className="flex items-center gap-1 text-base sm:text-lg font-semibold text-text hover:text-secondary transition duration-200 hover:scale-105 whitespace-nowrap px-2 py-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
                Instructor
              </Link>
              <Link to="/student-dashboard" className="flex items-center gap-1 text-base sm:text-lg font-semibold text-text hover:text-secondary transition duration-200 hover:scale-105 whitespace-nowrap px-2 py-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
                Student
              </Link>
            </>
          )}
          {currentUser && userRole === 'Instructor' && (
            <>
              <Link to="/instructor-dashboard" className="flex items-center gap-1 text-base sm:text-lg font-semibold text-text hover:text-secondary transition duration-200 hover:scale-105 whitespace-nowrap px-2 py-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
                Instructor
              </Link>
              <Link to="/student-dashboard" className="flex items-center gap-1 text-base sm:text-lg font-semibold text-text hover:text-secondary transition duration-200 hover:scale-105 whitespace-nowrap px-2 py-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
                Student
              </Link>
            </>
          )}
          {currentUser && userRole === 'student' && (
            <Link to="/student-dashboard" className="flex items-center gap-1 text-base sm:text-lg font-semibold text-text hover:text-secondary transition duration-200 hover:scale-105 whitespace-nowrap px-2 py-1 rounded-md">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
              Dashboard
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <ThemeSwitcher />
          {currentUser ? (
            <button onClick={logout} className="btn-raised bg-accent text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-dark">
              Logout
            </button>
          ) : (
            <Link to="/login" className="btn-raised bg-accent text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-dark">
              Login
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default App;
